import { apiClient } from '@/core/api/client';
import type {
  Client,
  ClientRelationshipResponse,
  PaginatedClientsResponse,
  PaginationMeta,
  ClientListParams,
} from '../types/client.types';

/** Map raw API response item to the flattened Client type */
const mapResponseToClient = (item: ClientRelationshipResponse): Client => ({
  id: item.clientUser.id,
  firstName: item.clientUser.firstName,
  lastName: item.clientUser.lastName,
  email: item.clientUser.email,
  profilePhoto: item.clientUser.profilePhoto ?? undefined,
  invitationMethod: item.invitationMethod,
  invitationReference: item.invitationReference ?? undefined,
  connectedAt: item.createdAt,
  lastLoginAt: item.clientUser.lastLoginAt ?? undefined,
});

export type PaginatedClients = {
  data: Client[];
  meta: PaginationMeta;
};

export const clientRelationshipsApi = {
  /**
   * Fetch paginated list of clients for the authenticated professional.
   * Calls GET /api/v1/clients
   */
  getClients: async (params?: ClientListParams): Promise<PaginatedClients> => {
    const { data } = await apiClient.get<PaginatedClientsResponse>('/clients', {
      params,
    });
    return {
      data: data.data.map(mapResponseToClient),
      meta: data.meta,
    };
  },

  /**
   * Fetch a single client relationship by client user ID.
   * Calls GET /api/v1/clients/:clientUserId
   */
  getClient: async (clientUserId: string): Promise<Client> => {
    const { data } = await apiClient.get<ClientRelationshipResponse>(
      `/clients/${clientUserId}`,
    );
    return mapResponseToClient(data);
  },

  /**
   * Remove a client (soft delete).
   * Calls DELETE /api/v1/clients/:clientUserId
   */
  removeClient: async (clientUserId: string): Promise<void> => {
    await apiClient.delete(`/clients/${clientUserId}`);
  },
};
